/**
 * Utility functions untuk format angka
 */

/**
 * Format angka sebagai mata uang Rupiah (tanpa simbol)
 * Contoh: 1000000 -> "1.000.000"
 */
export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null || value === 0) return '';
  return new Intl.NumberFormat('id-ID').format(value);
}

/**
 * Format angka untuk Bar Saldo (`SummaryBar` di atas tabel Buku Pengawasan).
 * - 0 → '0' — bukan kosong seperti `formatNumber`; nilai kosong terbaca
 *   "belum dimuat/gagal" (keputusan developer 24-10-2026). Selaras dengan
 *   Bar Saldo Akhir (`formatSaldoAkhir`) di bawah tabel.
 * - null / undefined → '' (data belum ada)
 * - Negatif → minus, sama dengan `formatNumber` (sengaja — lihat
 *   table-number-format-pattern.md § Pengecualian yang disengaja)
 *
 * Item "Selisih" tetap ditulis `ada ? formatNilaiBarSaldo(x) : '-'`.
 *
 * @see .claude/docs/Booku v2/ui-patterns/bar-saldo-pattern.md
 */
export function formatNilaiBarSaldo(value: number | undefined | null): string {
  if (value === 0) return '0';
  return formatNumber(value);
}

/**
 * Format angka dengan satuan minimal
 * Contoh: 1000000 -> "1.000.000" (tidak ada desimal jika bulat)
 */
export function formatNumberCompact(value: number | undefined | null): string {
  if (value === undefined || value === null || value === 0) return '';

  // Jika bilangan bulat, tampilkan tanpa desimal
  if (Number.isInteger(value)) {
    return new Intl.NumberFormat('id-ID').format(value);
  }

  // Jika ada desimal, tampilkan 2 angka di belakang koma
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format angka dengan prefix Rp
 * Contoh: 1000000 -> "Rp 1.000.000"
 */
export function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null || value === 0) return 'Rp 0';
  return `Rp ${new Intl.NumberFormat('id-ID').format(value)}`;
}

/**
 * Format angka desimal (selalu menampilkan 2 digit desimal)
 * Contoh: 1234.5 -> "1.234,50"
 * Digunakan untuk nilai mata uang asing yang memerlukan presisi desimal
 */
export function formatDecimal(value: number | undefined | null): string {
  if (value === undefined || value === null) return '';
  if (value === 0) return '0,00';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// ============================================================================
// Table Number Formatters (PATTERN BAKU untuk kolom angka di tabel)
//
// Aturan:
// - Zero / null / undefined → '-'
// - Negatif → kurung: (1.000.000) atau (1.234,56)
// - IDR: angka bulat, tanpa simbol "Rp"
// - MUA / Kurs: 2 desimal, format Indonesia (separator . untuk ribuan, , desimal)
//
// Pemakaian: lihat src/utils/tableColumnHelpers.ts (idrColumn, muaColumn, kursColumn).
// Dokumentasi pattern: .claude/docs/Booku v2/ui-patterns/table-number-format-pattern.md
// ============================================================================

/**
 * Format angka IDR untuk kolom tabel — angka bulat, no simbol.
 * - 0 / null / undefined → '-'
 * - Negatif → kurung
 *
 * Contoh:
 * - formatTableIDR(1000000)   → '1.000.000'
 * - formatTableIDR(-500000)   → '(500.000)'
 * - formatTableIDR(0)         → '-'
 */
export function formatTableIDR(value: number | undefined | null): string {
  if (value === undefined || value === null || value === 0 || Number.isNaN(value)) return '-';
  const formatted = new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return value < 0 ? `(${formatted})` : formatted;
}

/**
 * Format angka Mata Uang Asal (MUA) untuk kolom tabel — 2 desimal, no simbol.
 * - 0 / null / undefined → '-'
 * - Negatif → kurung
 *
 * Contoh:
 * - formatTableMUA(1234.5)    → '1.234,50'
 * - formatTableMUA(-1234.5)   → '(1.234,50)'
 * - formatTableMUA(0)         → '-'
 */
export function formatTableMUA(value: number | undefined | null): string {
  if (value === undefined || value === null || value === 0 || Number.isNaN(value)) return '-';
  const formatted = new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));
  return value < 0 ? `(${formatted})` : formatted;
}

/**
 * Format angka kurs untuk kolom tabel — alias dari formatTableMUA (2 desimal).
 *
 * Contoh:
 * - formatTableKurs(15731)    → '15.731,00'
 * - formatTableKurs(0)        → '-'
 */
export function formatTableKurs(value: number | undefined | null): string {
  return formatTableMUA(value);
}

/**
 * Format angka quantity untuk kolom tabel — angka bulat, no simbol.
 *
 * Saat ini perilakunya **identik** dengan `formatTableIDR`, tapi sengaja
 * dipisah sebagai function tersendiri untuk:
 * 1. Memperjelas semantik kolom (Quantity ≠ uang IDR)
 * 2. Memungkinkan pencabangan style di masa depan tanpa breaking change
 *    (mis. kalau perlu desimal untuk satuan tertentu, atau format compact "1.5 jt")
 *
 * Contoh:
 * - formatTableQuantity(150)  → '150'
 * - formatTableQuantity(0)    → '-'
 * - formatTableQuantity(-50)  → '(50)'
 */
export function formatTableQuantity(value: number | undefined | null): string {
  return formatTableIDR(value);
}

// ============================================================================
// Input Number Formatters / Parsers (PATTERN BAKU untuk textbox angka)
//
// Aturan (BERBEDA dengan formatter tabel — SENGAJA):
// - Negatif → minus: -1.500    (tabel pakai kurung)
// - Zero    → ''     (empty)   (tabel pakai '-')
// - Tidak pernah pakai simbol "Rp"
// - Separator standar Indonesia (. ribuan, , desimal)
//
// Pemakaian: dipasang di prop `formatter` & `parser` Antd <InputNumber>.
// Lihat src/components/common/inputs/ untuk komponen siap pakai.
// Dokumentasi pattern: .claude/docs/Booku v2/ui-patterns/textbox-pattern.md
// ============================================================================

/**
 * Format value untuk Antd InputNumber — varian INTEGER (IDR, Quantity).
 * - 0 / null / undefined / '' → ''
 * - Negatif → '-1.500'
 *
 * Catatan: Antd memanggil formatter dengan tipe `number | string`.
 */
export function formatInputInteger(
  value: number | string | undefined | null
): string {
  if (value === undefined || value === null || value === '') return '';
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num) || num === 0) return '';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

/**
 * Format value untuk Antd InputNumber — varian DESIMAL 2 digit (MUA, Kurs).
 * - 0 / null / undefined / '' → ''
 * - Negatif → '-16.524,37'
 * - **SELALU** 2 desimal (mis. `12` → `12,00`, `16.782` → `16.782,00`).
 *
 * Spek MUA & Kurs di Booku: 2 angka di belakang koma SELALU ditampilkan,
 * meskipun nilainya bilangan bulat. Konsisten dengan Booku Lama dan dengan
 * konvensi akuntansi mata uang asing.
 */
export function formatInputDecimal(
  value: number | string | undefined | null
): string {
  if (value === undefined || value === null || value === '') return '';
  const num = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(num) || num === 0) return '';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

/**
 * Parser value untuk Antd InputNumber — kembalikan string angka standar JS
 * (titik sebagai pemisah desimal) supaya Antd bisa parsing ke number.
 *
 * Buang separator ribuan ('.') dan ubah ',' jadi '.'.
 * Pertahankan tanda minus.
 *
 * Contoh: '1.500'      → '1500'
 *         '16.524,37'  → '16524.37'
 *         '-1.500'     → '-1500'
 *         ''           → ''
 */
export function parseInputNumber(
  value: string | undefined
): string {
  if (value === undefined || value === null || value === '') return '';
  // Buang semua karakter selain digit, minus, koma, titik
  const cleaned = String(value).replace(/[^\d,.\-]/g, '');
  // Hilangkan titik (separator ribuan), ganti koma jadi titik (desimal)
  return cleaned.replace(/\./g, '').replace(',', '.');
}
